import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';
import { counterSticky } from 'common/components/product-price-view/sticky.styles';
import { cn, jcm } from 'utils';
import { useIntersectionObserverCallback } from '@navi-toolbox/hooks';
import { useOptimizedImages } from 'common/hooks/use-optimized-images';
import { useBreakpoints } from 'common/hooks';
import { Skeleton } from 'fronton-react/unstable';
import { getDisplayedName, getProductImages, isWebSellable } from 'features/product-info/ducks/selectors';
import { ImagePlaceholder, ImageFallback } from 'common/components';
import { styles } from '../pdp-showcase/styles';
import { PdpPriceView } from '../pdp-price-view';
import { PdpAddToBasket } from '../pdp-add-to-basket';
import { mediaMobile } from '../pdp-media-gallery/styles';
import { PdpSelectTintColor } from '../pdp-select-tint-color';
import {
  fixedOuterContainerStyle,
  innerContainer,
  innerContainerFixed,
  thumbImg,
  unfixedOuterContainerStyleStyle,
  Basket,
  ToCart,
  SkeletonRows,
  bestPriceSkeleton,
  additionalPriceSkeleton,
  priceSkeleton,
  basketSkeleton,
} from './pdp-sticky-card-panel.styles';

export type PriceAndAddToBasketProps = {
  productDataIsLoading: boolean;
  isTintServiceAvailableInRegion: boolean;
};

export const StickyCardPanel: React.FC<PriceAndAddToBasketProps> = ({
  productDataIsLoading,
  isTintServiceAvailableInRegion,
}) => {
  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: false,
    largeDesktop: false,
  };
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const photoSrc = useSelector(getProductImages)?.[0].url ?? '';
  const productName = useSelector(getDisplayedName) ?? '';
  const imgSizes = {
    img: {
      w: '40',
      h: '40',
    },
  };
  const optimizedImage = useOptimizedImages(photoSrc, imgSizes);
  const isSellable = useSelector(isWebSellable);

  useEffect(() => {
    if (desktop || largeDesktop) {
      setIsSticky(false);
    } else if (window?.scrollY > (containerRef?.current?.offsetTop ? containerRef?.current?.offsetTop : 0)) {
      setIsSticky(true);
    }
  }, [containerRef, desktop, largeDesktop]);
  useIntersectionObserverCallback(
    containerRef,
    {
      exit: () => {
        if (desktop || largeDesktop) {
          setIsSticky(false);
        } else if (
          !isSticky &&
          window?.scrollY > (containerRef?.current?.offsetTop ? containerRef?.current?.offsetTop : 0)
        ) {
          setIsSticky(true);
        }
      },
      enter: () => {
        if (isSticky) setIsSticky(false);
      },
    },
    [isSticky, setIsSticky, containerRef],
  );

  return (
    <>
      <div className={unfixedOuterContainerStyleStyle} ref={containerRef}>
        {productDataIsLoading ? (
          <SkeletonRows>
            <Skeleton className={bestPriceSkeleton} />
            <Skeleton className={priceSkeleton} />
            <Skeleton className={additionalPriceSkeleton} />
            <Skeleton className={basketSkeleton} />
          </SkeletonRows>
        ) : (
          <>
            {isSellable && (
              <div className={innerContainer}>
                {isTintServiceAvailableInRegion && <PdpSelectTintColor />}
                <div data-qa="prices_mf-pdp" className={styles.prices}>
                  <PdpPriceView
                    isStickyPanel={false}
                    className={cn({
                      [styles.whenDataIsLoading.common]: productDataIsLoading,
                    })}
                  />
                </div>
                <Basket isSticky={false}>
                  <ToCart data-qa="to-cart_mf-pdp">
                    <PdpAddToBasket
                      size="l"
                      classnameCounter={styles.counter}
                      isStickyPanel={false}
                      className={jcm({
                        [styles.whenDataIsLoading.noninteractive]: productDataIsLoading,
                      })}
                    />
                  </ToCart>
                </Basket>
              </div>
            )}
          </>
        )}
      </div>
      {isSticky && !productDataIsLoading && (
        <div className={fixedOuterContainerStyle}>
          <div data-qa="photo_thumbnail_mf-pdp" className={thumbImg}>
            <picture data-qa="picture-thumbnail-sticky-element">
              <source
                media={mediaMobile}
                srcSet={`${optimizedImage.img?.xx} 2x, ${optimizedImage.img?.x} 1x`}
              />
              <ImagePlaceholder
                src={optimizedImage.img?.x || ''}
                srcSet={`${optimizedImage.img?.xx} 2x, ${optimizedImage.img?.x} 1x`}
                alt={productName}
                title={productName}
                className={thumbImg}
                loading="lazy"
                fallback={<ImageFallback width={40} height={40} />}
              />
            </picture>
          </div>
          <div className={cx(innerContainer, innerContainerFixed)}>
            <div data-qa="prices_mf-pdp-sticky">
              <PdpPriceView
                isStickyPanel={true}
                className={cn(styles.prices, {
                  [styles.whenDataIsLoading.common]: productDataIsLoading,
                })}
              />
            </div>
            <Basket isSticky={true}>
              <ToCart data-qa="to-cart_mf-pdp-sticky">
                <PdpAddToBasket
                  size="l"
                  classnameCounter={counterSticky}
                  isStickyPanel={true}
                  className={jcm({
                    [styles.whenDataIsLoading.noninteractive]: productDataIsLoading,
                  })}
                />
              </ToCart>
            </Basket>
          </div>
        </div>
      )}
    </>
  );
};
